/**
 * Cluster Management Hook
 * 提供集群列表、连通性自动检测和权限检测功能
 */

import { useState, useCallback } from 'react';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { ClusterListItem, ClusterCheckResult } from './types';

// 连通性状态
export interface ConnectivityStatus {
  status: 'connected' | 'error' | 'unknown';
  error?: string;
  checkedAt?: string;
}

export function useClusterManagement() {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  
  const [clusters, setClusters] = useState<ClusterListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkResult, setCheckResult] = useState<ClusterCheckResult | null>(null);
  const [checkLoading, setCheckLoading] = useState(false);
  const [fullCheckResults, setFullCheckResults] = useState<Map<string, ClusterCheckResult>>(new Map());

  // 连通性状态（批量自动检测）
  const [connectivity, setConnectivity] = useState<Record<string, ConnectivityStatus>>({});
  const [connectivityLoading, setConnectivityLoading] = useState(false);

  // 获取集群列表
  const fetchClusters = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
      const { token } = await identityApi.getCredentials();
      
      const response = await fetch(`${baseUrl}/cluster-management/clusters`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
        },
      });
      
      if (response.status === 304) {
        return;
      }
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch clusters: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      setClusters(data.clusters || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [discoveryApi, identityApi]);

  // 批量检测所有集群连通性（轻量级，只检测能否连上）
  const fetchConnectivity = useCallback(async () => {
    setConnectivityLoading(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
      const { token } = await identityApi.getCredentials();

      const response = await fetch(`${baseUrl}/cluster-management/connectivity`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setConnectivity(data.connectivity || {});
      }
    } catch {
      // 连通性检测失败不阻塞页面
    } finally {
      setConnectivityLoading(false);
    }
  }, [discoveryApi, identityApi]);

  // 检测指定集群的权限（完整 RBAC 检测）
  const checkCluster = useCallback(async (clusterName: string, forceRefresh = false) => {
    setCheckLoading(true);
    setCheckResult(null);
    
    try {
      const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
      const { token } = await identityApi.getCredentials();
      
      const url = `${baseUrl}/cluster-management/check/${encodeURIComponent(clusterName)}${forceRefresh ? '?refresh=true' : ''}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to check cluster: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      setCheckResult(result);
      
      // 缓存完整检测结果
      setFullCheckResults(prev => {
        const newMap = new Map(prev);
        newMap.set(clusterName, result);
        return newMap;
      });

      // 同步更新连通性状态
      setConnectivity(prev => ({
        ...prev,
        [clusterName]: {
          status: result.connectionStatus,
          checkedAt: result.checkedAt,
        },
      }));
      
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setConnectivity(prev => ({
        ...prev,
        [clusterName]: {
          status: 'error',
          error: err instanceof Error ? err.message : String(err),
          checkedAt: new Date().toISOString(),
        },
      }));
      return null;
    } finally {
      setCheckLoading(false);
    }
  }, [discoveryApi, identityApi]);

  // 获取缓存的完整检测结果
  const getCheckResult = useCallback((clusterName: string): ClusterCheckResult | undefined => {
    return fullCheckResults.get(clusterName);
  }, [fullCheckResults]);

  // 获取 kubeconfig
  const getKubeconfig = useCallback(async (clusterName: string): Promise<string | null> => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
      const { token } = await identityApi.getCredentials();
      
      const url = `${baseUrl}/cluster-management/kubeconfig/${encodeURIComponent(clusterName)}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get kubeconfig: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      return data.kubeconfig;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return null;
    }
  }, [discoveryApi, identityApi]);

  return {
    clusters,
    loading,
    error,
    checkResult,
    checkLoading,
    connectivity,
    connectivityLoading,
    fetchClusters,
    fetchConnectivity,
    checkCluster,
    getCheckResult,
    getKubeconfig,
  };
}
